import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Calendar, FileText, 
  Share2, Printer, ZoomIn, ZoomOut, Maximize, BookOpen
} from 'lucide-react';
import ArticleCard from '../components/news/ArticleCard';
import BotonDescargarPdf from '../components/BotonDescargarPdf';
import { editionService } from '../services/api';
import { useNewspaper } from '../context/AppContext';
import styles from './EditionDetailPage.module.css';

export default function EditionDetailPage() {
  const { number } = useParams();
  const { categories } = useNewspaper();
  
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('articles'); // articles | pdf
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    loadEdition();
  }, [number]);

  const loadEdition = async () => {
    try {
      setLoading(true);
      const data = await editionService.getByNumber(parseInt(number));
      setEdition(data);
    } catch (error) {
      console.error('Error loading edition:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Edición N° ${edition.number}`,
        text: `Lee la edición N° ${edition.number} del periódico`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  // Agrupar artículos por sección
  const articlesBySection = edition?.articles?.reduce((acc, article) => {
    const sectionSlug = article.category?.slug || 'otros';
    if (!acc[sectionSlug]) {
      acc[sectionSlug] = {
        category: article.category || { name: 'Otros', slug: 'otros' },
        articles: []
      };
    }
    acc[sectionSlug].articles.push(article);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando edición...</p>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className={styles.notFound}>
        <h1>Edición no encontrada</h1>
        <p>La edición que buscas no existe.</p>
        <Link to="/ediciones" className="btn btn-primary">
          Ver todas las ediciones
        </Link>
      </div>
    );
  }

  return (
    <main className={styles.editionDetail}>
      {/* Navegación */}
      <nav className={styles.breadcrumb}>
        <div className="container">
          <Link to="/">Inicio</Link>
          <ChevronRight size={14} />
          <Link to="/ediciones">Ediciones</Link>
          <ChevronRight size={14} />
          <span>Edición N° {edition.number}</span>
        </div>
      </nav>

      {/* Header */}
      <header className={styles.header}>
        <div className="container">
          <div className={styles.headerGrid}>
            {/* Portada */}
            <div className={styles.coverWrapper}>
              {edition.coverImage ? (
                <img 
                  src={edition.coverImage} 
                  alt={`Portada edición ${edition.number}`}
                  className={styles.coverImage}
                />
              ) : (
                <div className={styles.coverPlaceholder}>
                  <FileText size={48} />
                  <span>Edición N° {edition.number}</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className={styles.headerInfo}>
              <span className={styles.editionLabel}>Edición Impresa</span>
              <h1 className={styles.editionNumber}>
                Edición N° {edition.number}
              </h1>
              <p className={styles.editionDate}>
                <Calendar size={18} />
                {formatDate(edition.publishedAt)}
              </p>
              <div className={styles.editionStats}>
                <span>
                  <FileText size={16} />
                  {edition.pages?.length || 0} páginas
                </span>
                <span>
                  {edition.articles?.length || 0} artículos
                </span>
              </div>

              {/* Acciones */}
              <div className={styles.actions}>
                <BotonDescargarPdf edition={edition} variant="primary" />
                <Link
                  to={`/edicion/${edition.id}/revista`}
                  className="btn btn-outline"
                  title="Ver en modo revista"
                >
                  <BookOpen size={18} />
                  Modo Revista
                </Link>
                <button 
                  className="btn btn-outline"
                  onClick={handlePrint}
                >
                  <Printer size={18} />
                  Imprimir
                </button>
                <button 
                  className="btn btn-outline"
                  onClick={handleShare}
                >
                  <Share2 size={18} />
                  Compartir
                </button>
              </div>

              {/* Navegación entre ediciones */}
              <div className={styles.editionNav}>
                {edition.previousNumber && (
                  <Link 
                    to={`/ediciones/${edition.previousNumber}`}
                    className={styles.navLink}
                  >
                    <ChevronLeft size={16} />
                    Edición anterior
                  </Link>
                )}
                {edition.nextNumber && (
                  <Link 
                    to={`/ediciones/${edition.nextNumber}`}
                    className={styles.navLink}
                  >
                    Siguiente edición
                    <ChevronRight size={16} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs de vista */}
      <div className={styles.viewTabs}>
        <div className="container">
          <div className={styles.tabsInner}>
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${viewMode === 'articles' ? styles.active : ''}`}
                onClick={() => setViewMode('articles')}
              >
                Ver artículos
              </button>
              <button
                className={`${styles.tab} ${viewMode === 'pdf' ? styles.active : ''}`}
                onClick={() => setViewMode('pdf')}
              >
                Vista de páginas
              </button>
            </div>

            {viewMode === 'pdf' && (
              <div className={styles.zoomControls}>
                <button onClick={() => setZoom(z => Math.max(50, z - 10))}>
                  <ZoomOut size={18} />
                </button>
                <span>{zoom}%</span>
                <button onClick={() => setZoom(z => Math.min(200, z + 10))}>
                  <ZoomIn size={18} />
                </button>
                <button onClick={() => setZoom(100)}>
                  <Maximize size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contenido */}
      <section className={styles.content}>
        <div className="container">
          {viewMode === 'articles' ? (
            /* Vista de artículos */
            <div className={styles.articlesSections}>
              {Object.entries(articlesBySection).map(([slug, { category, articles }]) => (
                <div key={slug} className={styles.section}>
                  <h2 
                    className={styles.sectionTitle}
                    style={{ '--section-color': category.color }}
                  >
                    <span></span>
                    {category.name}
                  </h2>
                  <div className={styles.articlesGrid}>
                    {articles.map(article => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        variant="default"
                        showExcerpt={true}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {Object.keys(articlesBySection).length === 0 && (
                <div className={styles.empty}>
                  <p>Esta edición no tiene artículos asociados</p>
                </div>
              )}
            </div>
          ) : (
            /* Vista de PDF/páginas */
            <div className={styles.pdfViewer}>
              <div 
                className={styles.pdfContainer}
                style={{ transform: `scale(${zoom / 100})` }}
              >
                {/* Simulación de páginas - en producción sería un visor PDF real */}
                <div className={styles.pdfPage}>
                  {edition.coverImage ? (
                    <img src={edition.coverImage} alt="Portada" />
                  ) : (
                    <div className={styles.pagePlaceholder}>
                      <p>Página 1 - Portada</p>
                    </div>
                  )}
                </div>
                {Array.from({ length: (edition.pages?.length || 1) - 1 }, (_, i) => (
                  <div key={i} className={styles.pdfPage}>
                    <div className={styles.pagePlaceholder}>
                      <p>Página {i + 2}</p>
                      <span>Contenido de la edición</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className={styles.pdfNote}>
                Para ver la edición completa en PDF, descarga el archivo usando el botón superior.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Índice de la edición */}
      <aside className={styles.tableOfContents}>
        <div className="container">
          <h3 className={styles.tocTitle}>Índice de esta edición</h3>
          <div className={styles.tocGrid}>
            {Object.entries(articlesBySection).map(([slug, { category, articles }]) => (
              <div key={slug} className={styles.tocSection}>
                <h4 style={{ color: category.color }}>{category.name}</h4>
                <ul>
                  {articles.slice(0, 3).map(article => (
                    <li key={article.id}>
                      <Link to={`/articulo/${article.slug}`}>
                        {article.title}
                      </Link>
                    </li>
                  ))}
                  {articles.length > 3 && (
                    <li className={styles.moreLink}>
                      +{articles.length - 3} más
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </aside>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}
